import { useRef, useState } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { RefreshCw, Sparkles } from "lucide-react";
import { theme } from "../theme";
import { api, ApiError } from "../api/client";
import { TierLockModal, TierErrorPayload, TIER_NAMES } from "./TierLockModal";

interface Props {
  controlId: string;
  controlName: string;
  framework: string;
  status: string;
  notes?: string;
  guidance?: string[];
}

type State = "idle" | "loading" | "done" | "error";

// The route returns structured steps; rows cached before the column was widened
// (and very old runs) hold plain strings. Render either.
interface RemediationStep {
  action:     string;
  ownerRole?: string;
  effortDays?: number;
  priority?:  string;
  category?:  string;
}

function normaliseStep(rec: RemediationStep | string): RemediationStep {
  return typeof rec === "string" ? { action: rec } : rec;
}

export function AiRecommendations({ controlId, controlName, framework, status, notes, guidance }: Props) {
  const navigate = usePortalNavigate();
  const [state, setState] = useState<State>("idle");
  const [recommendations, setRecommendations] = useState<RemediationStep[]>([]);
  const [error, setError] = useState("");
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  async function load() {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState("loading");
    setError("");
    try {
      const d = await api.postAbortable<{ recommendations: Array<RemediationStep | string> }>("/guidance/recommendations", {
        framework,
        controlId,
        controlName,
        status,
        notes: notes || undefined,
        guidance: guidance?.length ? guidance : undefined,
      }, controller.signal);
      setRecommendations((d.recommendations ?? []).map(normaliseStep));
      setState("done");
    } catch (e) {
      if (controller.signal.aborted) { setState("idle"); return; }
      if (e instanceof ApiError && e.status === 402) {
        setTierError(e.payload as TierErrorPayload);
        setState("error");
      } else {
        setError(e instanceof ApiError ? e.message : "Could not generate recommendations.");
        setState("error");
      }
    }
  }

  function cancel() {
    abortRef.current?.abort();
    setState("idle");
  }

  return (
    <>
    {tierError && (
      <TierLockModal
        featureName="AI Recommendations"
        requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
        requiredTierLevel={tierError.requiredLevel}
        currentTierLevel={tierError.currentLevel}
        onClose={() => setTierError(null)}
        onRequestUpgrade={() => navigate("/my-services")}
      />
    )}

    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${theme.color.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: state === "done" ? 10 : 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", flex: 1 }}>
          AI Recommended Actions
        </div>
        {state !== "loading" && (
          <button
            onClick={load}
            style={{
              background: "transparent",
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3,
              padding: "4px 10px",
              fontSize: 11,
              color: theme.color.muted,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {state === "done"
              ? <><RefreshCw size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />Refresh</>
              : <><Sparkles size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />Get recommendations</>
            }
          </button>
        )}
        {state === "loading" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: theme.color.faint }}>Generating…</span>
            <button onClick={cancel} style={{ background: "transparent", border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "2px 7px", fontSize: 10, color: theme.color.dangerText, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {state === "idle" && (
        <p style={{ margin: 0, fontSize: 12, color: theme.color.faint, fontStyle: "italic" }}>
          Click to generate AI-tailored recommendations for this control based on your organisation's profile and evidence.
        </p>
      )}

      {state === "error" && !tierError && (
        <p style={{ margin: 0, fontSize: 12, color: theme.color.dangerText }}>{error || "Something went wrong."}</p>
      )}

      {state === "done" && recommendations.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {recommendations.map((rec, i) => {
            const meta = [
              rec.ownerRole || null,
              rec.effortDays ? `${rec.effortDays} day${rec.effortDays === 1 ? "" : "s"}` : null,
              rec.priority || null,
              rec.category || null,
            ].filter(Boolean);
            return (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: theme.color.text, alignItems: "flex-start" }}>
                <span style={{ color: "#f59e0b", flexShrink: 0, fontWeight: 700, marginTop: 1 }}>→</span>
                <div style={{ lineHeight: 1.55 }}>
                  <div>{rec.action}</div>
                  {meta.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>{meta.join(" · ")}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}
